import React, { useCallback } from "react";
import { debounce } from "cayo.ui";
import { SavedQuery } from "@cayo/shared/api/types/cayo-graph";
import { IQueryFilterParameters } from "@cayo/shared/components/QueryFilterForm/IQueryFilterParameters";
import logger from "@cayo/shared/libs/logger";
import { urlUtils } from "@cayo/shared/utils/url";
import { makeErrorMessage, savedQueries } from "./logic";
import { filterSignalsService } from "../../services/filter-signal.service";
import { gridUtils } from "../../utils/grid-utils";

const log = logger.getLogger("QUICK-FILTER QueryFilterForm.hooks");

const useHideSaveSuccessInfo = () => {
  const [saveSuccess, setSaveSuccess] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    debounce(() => {
      setSaveSuccess(undefined);
    }, 5000)();
  }, [saveSuccess]);

  return { saveSuccess, setSaveSuccess };
};

const useFormErrors = () => {
  const [errors, setErrors] = React.useState<{ [id: string]: string }>({});

  const setErrorCallback = useCallback(
    (name: string, e: any) => {
      if (e) {
        errors[name!] = e;
      } else if (errors) {
        delete errors[name!];
      }
      setErrors({ ...errors });
    },
    [errors]
  );

  return { errors, setErrors, setErrorCallback };
};

function deleteFilter(
  url: string,
  query: SavedQuery,
  gridId: string,
  prms: IQueryFilterParameters,
  showConfirmation: (message: string, onConfirmed: () => Promise<void>, warning?: string) => void,
  hideConfirmation: () => void,
  onClose: () => void,
  setError: (e: string) => void
) {
  const { onFilterSaved, saveOnServerOnly } = prms;

  const gridSettingsKey = urlUtils.trim(window.location.hash + gridId);

  showConfirmation(`Are you sure you want to delete '${query.name}' saved query?`, () => {
    hideConfirmation();

    return savedQueries
      .delete(url!, query.id!)
      .then(() => {
        if (onFilterSaved) {
          onFilterSaved();
        }

        if (!saveOnServerOnly || gridUtils.getFilter(gridSettingsKey)?.id === query.id) {
          filterSignalsService.send("filterSelected", undefined, gridId);
        }

        onClose && onClose();
      })
      .catch((e) => {
        setError(makeErrorMessage(e));
      });
  });
}

export const savedQueryModelReducer = (old: SavedQuery, newModel: SavedQuery) => {
  const result = { ...old, ...newModel };
  if (log.isDebugEnabled()) {
    log.debug("savedQueryModelReducer -> ", { old, newModel, result });
  }
  return result;
};

export const queryFilterFormHooks = {
  useHideSaveSuccessInfo,
  useFormErrors,
  deleteFilter,
  savedQueryModelReducer,
};
